import { useEffect, useState, useCallback } from 'react'
import { initLiff, isLoggedIn, loginWithLine, getLiffProfile } from '../lib/liff'
import { api } from '../lib/api'
import { useAppStore } from '../store/useAppStore'

const REF_KEY = 'amulet_pending_ref'

/** อ่าน ?ref=XXXX จาก URL ตอนเปิดแอป → เก็บไว้รอ apply หลัง login */
function capturePendingRef(): void {
  try {
    const params = new URLSearchParams(window.location.search)
    const ref = params.get('ref')
    if (ref && /^[A-Z0-9]{4,8}$/i.test(ref)) {
      localStorage.setItem(REF_KEY, ref.toUpperCase())
    }
  } catch {}
}

async function consumePendingRef(lineUid: string): Promise<void> {
  try {
    const ref = localStorage.getItem(REF_KEY)
    if (!ref) return
    // ลบทันทีไม่ว่าสำเร็จ/ล้มเหลว — กัน retry loop
    localStorage.removeItem(REF_KEY)
    await api.referral.apply(lineUid, ref).catch(err => {
      console.warn('[Referral] apply failed (silent)', (err as Error).message)
    })
  } catch {}
}

export function useLiffAuth() {
  const { lineUid, setLineUid, setProfile } = useAppStore()
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refreshProfile = useCallback(async (uid: string) => {
    try {
      const profile = await api.user.profile(uid) as Parameters<typeof setProfile>[0]
      setProfile(profile)
    } catch (err) {
      console.warn('[useLiffAuth] Profile refresh failed', err)
    }
  }, [setProfile])

  useEffect(() => {
    async function init() {
      try {
        // เก็บ ref param ก่อนทำอะไรเลย — login redirect อาจล้าง URL
        capturePendingRef()

        await initLiff()

        if (!import.meta.env.VITE_LIFF_ID) {
          setLineUid('dev-user-001')
          setReady(true)
          return
        }

        if (!isLoggedIn()) {
          loginWithLine()
          return
        }

        const lineProfile = await getLiffProfile()
        if (!lineProfile) throw new Error('Cannot get LINE profile')

        const uid = lineProfile.userId

        await api.user.upsert(uid, lineProfile.displayName, lineProfile.pictureUrl)

        // Apply ref code ทันทีหลัง user upsert — ก่อน profile fetch
        await consumePendingRef(uid)

        const profile = await api.user.profile(uid) as Parameters<typeof setProfile>[0]
        setLineUid(uid)
        setProfile(profile)
        setReady(true)
      } catch (err) {
        console.error('[useLiffAuth]', err)
        setError(err instanceof Error ? err.message : 'Login failed')
        setReady(true)
      }
    }

    init()
  }, [setLineUid, setProfile])

  return { ready, error, lineUid, refreshProfile }
}
